'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DesktopHeader } from '@/app/_components/DesktopHeader';
import { FloatingBnbController } from '@/app/_components/FloatingBnbController';
import { GlobalModal } from '@/app/_components/GlobalModal';
import { GlobalNavigation } from '@/app/_components/GlobalNavigation';
import { ToastProvider } from '@/app/_components/ToastProvider';
import { ToastSearchParamsController } from '@/app/_components/ToastSearchParamsController';
import { PageNavigationHost } from '@/components/client/Layout/PageNavigation';
import { SELECTOR } from '@/constants';
import { useGlobalStore } from '@/stores/GlobalStore';
import { findRoute } from '@/utils';
import { HIDE_DESKTOP_HEADER_PATHNAMES } from './constants';
import { ResponsiveDialogContainer } from './ResponsiveDialogContainer';

// SSR 비활성화로 App Router 마운트 이슈 해결
const NaverCtsTracker = dynamic(() => import('./NaverCtsTracker').then(mod => ({
  default: mod.NaverCtsTracker
})), {
  ssr: false
});
interface GlobalLayoutProps {
  children: React.ReactNode;
}
const OptionalGlobalFeatureBoundary = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ErrorBoundary fallback={null}>{children}</ErrorBoundary>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const GlobalLayout = t0 => {
  const $ = _c(23);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const currentRoute = findRoute(pathname);
  const hideBottomNav = useGlobalStore(_temp);
  let t1;
  if ($[0] !== pathname) {
    t1 = HIDE_DESKTOP_HEADER_PATHNAMES.includes(pathname ?? "");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const showDesktopHeader = !t1;
  const hasBottomNav = !hideBottomNav && (currentRoute?.hasBottomNav ?? true);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <a href={`#${SELECTOR.MAIN}`} className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[9999] focus:rounded-small focus:bg-Gray-black focus:px-16 focus:py-8 focus:font-600 focus:text-Gray-white focus:typo-body4">본문 바로가기</a>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== showDesktopHeader) {
    t3 = showDesktopHeader && <DesktopHeader />;
    $[3] = showDesktopHeader;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = showDesktopHeader && "has-[article[data-header-offset-page]]:min-h-[calc(100dvh-4.5rem-1px)] mobile-tablet:has-[article[data-header-offset-page]]:min-h-dvh";
  let t5;
  if ($[5] !== t4) {
    t5 = cn("relative flex min-h-dvh max-w-full flex-col overflow-x-clip bg-Gray-white", t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <PageNavigationHost />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <OptionalGlobalFeatureBoundary><ResponsiveDialogContainer /></OptionalGlobalFeatureBoundary>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const T0 = ToastProvider;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ToastSearchParamsController />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = cn("relative flex flex-1 flex-col", {
    "mobile-tablet:pb-[calc(70px+var(--safe-area-bottom,0px))]": hasBottomNav
  });
  let t10;
  if ($[10] !== children) {
    t10 = <Suspense fallback={null}>{children}</Suspense>;
    $[10] = children;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Suspense fallback={null}><OptionalGlobalFeatureBoundary><NaverCtsTracker /></OptionalGlobalFeatureBoundary></Suspense>;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t10 || $[14] !== t9) {
    t12 = <main className={t9} id={SELECTOR.MAIN}>{t10}{t11}</main>;
    $[13] = t10;
    $[14] = t9;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== T0 || $[17] !== t12 || $[18] !== t8) {
    t13 = <T0>{t8}{t12}</T0>;
    $[16] = T0;
    $[17] = t12;
    $[18] = t8;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <GlobalModal />;
    t15 = <GlobalNavigation />;
    $[20] = t14;
    $[21] = t15;
  } else {
    t14 = $[20];
    t15 = $[21];
  }
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <OptionalGlobalFeatureBoundary><FloatingBnbController /></OptionalGlobalFeatureBoundary>;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  return <>{t2}{t3}<div className={t5} id={SELECTOR.MAIN_WRAPPER}><div className={cn("sticky top-0 z-20 hidden bg-Gray-white mobile-tablet:block", currentRoute?.hasPageNav && "min-h-48")} id={SELECTOR.PAGE_NAV_WRAPPER}>{t6}</div>{t7}{t13}{t14}{t15}{t16}</div></>;
};
function _temp(state) {
  return state.hideBottomNav;
}